import { ChangeDetectionStrategy, Component, computed, inject, input, TemplateRef, viewChild } from '@angular/core';
import { SIDEBAR_CONTEXT } from '../../../core/services/sidebar/sidebar-context';
import { SidebarService } from '../../../core/services/sidebar/sidebar.service';
import { TaskViewSidebarWrapperComponent } from 'src/app/rent-module/containers/task-view/task-view-sidebar-wrapper.component';
import { Task } from 'src/app/rent-module/shared/entities/task/task';
import { TasksService } from 'src/app/rent-module/shared/services/tasks/tasks.service';

@Component({
  selector: 'div[task-row-v2], app-task-row-v2-component',
  templateUrl: './task-row-v2.component.html',
  styleUrls: ['./task-row-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'class': 'block border-b border-gray first:border-t',
    '[class.last:border-b-0]': 'hideLastBorder()'
  }
})
export class TaskRowV2Component {
  private readonly sidebarService = inject(SidebarService);
  protected readonly tasksService = inject(TasksService);

  // Sidebar context - true if this component is rendered inside a sidebar
  private readonly isInSidebar = inject(SIDEBAR_CONTEXT, { optional: true }) ?? false;

  // Default fields to display
  private readonly DEFAULT_FIELDS = ['address', 'order', 'customer', 'resources'];

  // Signal-based inputs
  readonly task = input.required<Task>();
  readonly isLast = input(false); 
  readonly hideLastBorder = input(false);
  readonly displayedFields = input<string[]>(this.DEFAULT_FIELDS);
  readonly showMetadata = input<boolean>(false);
  readonly showLabels = input<boolean>(true);

  // Template references for field rendering
  protected readonly orderTpl = viewChild<TemplateRef<any>>('orderTpl');
  protected readonly customerTpl = viewChild<TemplateRef<any>>('customerTpl');
  protected readonly addressTpl = viewChild<TemplateRef<any>>('addressTpl');
  protected readonly resourcesTpl = viewChild<TemplateRef<any>>('resourcesTpl');
  protected readonly postalCodeTpl = viewChild<TemplateRef<any>>('postalCodeTpl');
  protected readonly cityTpl = viewChild<TemplateRef<any>>('cityTpl');

  // Computed signal for selection state (split-view highlighting)
  readonly isSelected = computed(() =>
    this.sidebarService.selectedEntityId() === this.task()?.id
  );

  // Computed signals for derived state
  protected readonly taskColor = computed(() => this.task()?.taskType?.color);
  protected readonly dueDateTime = computed(() => this.tasksService.getDueDateTimeDisplay(this.task()?.dueDateTimePeriod));
  protected readonly taskTitle = computed(() => this.task()?.title || '-');
  protected readonly createdByName = computed(() => {
    const task = this.task();
    console.log('Computing createdByName for task:', task);
    return `${task?.createdBy?.firstName ?? ''} ${task?.createdBy?.lastName ?? ''}`.trim();
  });
  protected readonly stateLabel = computed(() => this.task()?.getStateLabel() ?? '');
  protected readonly stateSeverity = computed(() => this.task()?.getStateSeverity());
  protected readonly isCompleted = computed(() => this.task()?.state === 'COMPLETED');
  protected readonly showMetadataRow = computed(() => this.showMetadata());
  protected readonly customer = computed(() => this.task()?.linkedContact);
  protected readonly address = computed(() => this.task()?.address);
  protected readonly resources = computed(() => this.task()?.linkedResources);
  protected readonly order = computed(() => this.task()?.order);
  
  // Tooltip für Address: zeigt name und/oder note wenn gesetzt
  protected readonly addressTooltip = computed(() => {
    const addr = this.address();
    if (!addr) return '';
    
    const parts = [];
    if (addr.name) parts.push(addr.name);
    if (addr.note) parts.push(addr.note);
    
    return parts.join('<br>');
  });
  
  // Filter displayedFields to only include fields with actual values
  protected readonly visibleFields = computed(() => {
    const task = this.task();
    const fields = this.displayedFields();
    
    return fields.filter(fieldName => {
      switch(fieldName) {
        case 'order':
          return task?.order?.number;
        case 'customer':
          return task?.linkedContact?.id;
        case 'address':
          return task?.address;
        case 'resources':
          return task?.linkedResources && task.linkedResources.length > 0;
        case 'postalCode':
          return task?.address?.postalCode;
        case 'city':
          return task?.address?.city;
        default:
          return false;
      }
    });
  });
  
  protected readonly showAdditionalInfoRow = computed(() => {
    return !this.showMetadataRow() || this.visibleFields().length > 0;
  });
  
  protected readonly showLabelsRow = computed(() => 
    this.showLabels() && this.task()?.labels && this.task()!.labels.length > 0
  );
  
  // Transform TaskAssignment to AssignmentData format
  protected readonly assignmentData = computed(() => {
    const assignment = this.task()?.assignment;
    if (!assignment) return null;
    return {
      id: assignment.assigneeId,
      displayName: assignment.name,
      assignmentType: assignment.assignmentType
    };
  });

  getFieldTemplate(fieldName: string): TemplateRef<any> | null {
    switch(fieldName) {
      case 'order':
        return this.orderTpl() ?? null;
      case 'customer':
        return this.customerTpl() ?? null;
      case 'address':
        return this.addressTpl() ?? null;
      case 'resources':
        return this.resourcesTpl() ?? null;
      case 'postalCode':
        return this.postalCodeTpl() ?? null;
      case 'city':
        return this.cityTpl() ?? null;
      default:
        return null;
    }
  }

  openTask(): void {
    if (!this.isInSidebar) {
      this.sidebarService.open(TaskViewSidebarWrapperComponent, { task: this.task() });
    }
  }
}
